import { useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { Timer } from './Timer';
import { Lifelines } from './Lifelines';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { Question } from '../types/game.types';

interface Props {
  question: Question;
  onSubmit: (answerId: string) => void;
  onUseLifeline: (type: string) => void;
}

export const QuestionCard = ({ question, onSubmit, onUseLifeline }: Props) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const { myAnswer, timeRemaining, currentQuestionIndex, totalQuestions } = useGameStore();
  
  const hasAnswered = myAnswer !== null;
  const isTimeUp = timeRemaining === 0;

  const handleSubmit = () => {
    if (selectedAnswer && !hasAnswered) {
      onSubmit(selectedAnswer);
    }
  };

  return (
    <Card className="p-8 space-y-8 shadow-xl border-2 border-accent-greenLight/30">
      {/* Timer and Question Number */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 px-4 py-2 rounded-lg">
            <span className="text-sm text-gray-600 font-medium">
              السؤال {currentQuestionIndex + 1} من {totalQuestions}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Timer seconds={timeRemaining} />
        </div>
      </div>

      {/* Question Text */}
      <div className="bg-gradient-to-br from-accent-greenLight/20 to-primary/20 rounded-lg p-6">
        <h2 className="text-3xl font-bold text-center text-primary leading-relaxed" style={{ fontFamily: "'Almarai', 'Cairo', sans-serif" }}>
          {question.text}
        </h2>
      </div>

      {/* Answers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {question.answers.map((answer, index) => {
          const isSelected = selectedAnswer === answer.id;
          const isCorrect = answer.isCorrect;
          const isWrong = isSelected && !isCorrect && isTimeUp;
          
          return (
            <Button
              key={answer.id}
              variant={
                isTimeUp
                  ? isCorrect
                    ? 'default'
                    : isWrong
                    ? 'destructive'
                    : 'outline'
                  : isSelected
                  ? 'default'
                  : 'outline'
              }
              onClick={() => !hasAnswered && !isTimeUp && setSelectedAnswer(answer.id)}
              disabled={hasAnswered || isTimeUp}
              className={`h-24 text-lg font-semibold transition-all ${
                isSelected && !isTimeUp
                  ? 'bg-secondary hover:bg-secondary-dark text-white border-2 border-secondary'
                  : isTimeUp && isCorrect
                  ? 'bg-green-500 hover:bg-green-600 text-white border-2 border-green-600'
                  : isWrong
                  ? 'bg-red-500 hover:bg-red-600 text-white border-2 border-red-600'
                  : 'hover:bg-primary/5'
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="text-2xl">{String.fromCharCode(65 + index)}.</span>
                <span>{answer.text}</span>
                {isTimeUp && isCorrect && <span className="text-xl">✓</span>}
                {isWrong && <span className="text-xl">✗</span>}
              </span>
            </Button>
          );
        })}
      </div>

      {/* Actions */}
      {!hasAnswered && !isTimeUp && (
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <Lifelines onUse={onUseLifeline} />
          <Button
            onClick={handleSubmit}
            disabled={!selectedAnswer}
            size="lg"
            className="bg-secondary hover:bg-secondary-dark text-white px-8 py-6 text-lg font-bold"
          >
            إرسال الإجابة
          </Button>
        </div>
      )}

      {hasAnswered && !isTimeUp && (
        <div className="bg-accent-greenLight/20 border-2 border-accent-greenLight rounded-lg p-4 text-center">
          <p className="text-accent-greenDark font-semibold text-lg">
            ✓ تم إرسال الإجابة! في انتظار اللاعبين الآخرين...
          </p>
        </div>
      )}

      {isTimeUp && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-600 font-semibold text-lg">
            انتهى الوقت! جاري عرض الإجابة الصحيحة...
          </p>
        </div>
      )}
    </Card>
  );
};
